import { AvatarDropdown, AvatarName } from '@/components';
import { currentUser as queryCurrentUser, getUserSideberApi, getNotificationApi, type UserSidebarMenuItem } from '@/services/system/api';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { setUserAccessPaths } from './access';
const loginPath = '/user/login';
const defualtAvatar = '/avatar-girl.png';
const chatSessionsMenuKey = 'chat_sessions';
import { Alert, FloatButton, ConfigProvider, App as AntdApp } from 'antd';
import Marquee from 'react-fast-marquee';
import IframeListener from '@/components/IframeListener';
import { setAntdInstances } from '@/utils/antdInstance';
import { getToken } from '@/utils/util';
import HistoryMenu from '@/components/HistoryMenu';
import SidebarCreateEntry from '@/components/SidebarCreateEntry';
// 存储用户有权限访问的路径
let userAccessPaths: string[] = [];
let hiddenMenus: string[] = [];

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  userMenus?: string[]; // 添加用户菜单数据
  hiddenMenus?: string[];
}> {
  const getUserSideberFun = async () => {
    try {
      const res = await getUserSideberApi();
      // 提取用户有权限的路径
      userAccessPaths = [];
      hiddenMenus = Array.isArray(res.data?.hidden_menus) ? res.data.hidden_menus : [];
      const extractPaths = (menus: UserSidebarMenuItem[]) => {
        menus.forEach((menu) => {
          if (menu?.path && menu?.display === 1) {
            userAccessPaths.push(`${menu.path}`);
          }
          if (menu?.children?.length) {
            extractPaths(menu.children);
          }
        });
      };
      if (Array.isArray(res.data?.items)) {
        extractPaths(res.data.items);
        setUserAccessPaths(userAccessPaths);
      }
      return res.data?.items || [];
    } catch (error) {
      console.error('获取用户菜单失败:', error);
      return [];
    }
  };

  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      await getUserSideberFun();
      return msg.data;
    } catch (error) {
      // todo
    }
    return undefined;
  };

  // 登录页有 token 时先校验用户信息，避免已登录用户停留在登录页。
  const { location } = history;
  if (location.pathname === loginPath) {
    if (getToken()) {
      const currentUser = await fetchUserInfo();
      if (currentUser) {
        history.replace('/');
        return {
          fetchUserInfo,
          currentUser,
          settings: defaultSettings as Partial<LayoutSettings>,
          userMenus: userAccessPaths,
          hiddenMenus,
        };
      }
    }

    return {
      fetchUserInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
    userMenus: userAccessPaths, // 添加用户菜单数据
    hiddenMenus,
  };
}
function NotificationBanner() {
  const [notificationText, setNotificationText] = React.useState('');
  React.useEffect(() => {
    async function fetchNotification() {
      const mockdata = await getNotificationApi();
      if (mockdata?.data?.message) {
        setNotificationText(mockdata.data.message);
      } else {
        setNotificationText('');
      }
    }
    fetchNotification();
  }, []);
  return (
    <>
      {notificationText && (
        <Alert
          banner
          message={
            <Marquee pauseOnHover gradient={false}>
              <span dangerouslySetInnerHTML={{ __html: notificationText }} />
            </Marquee>
          }
        />
      )}
    </>
  );
}

function AntdAppProvider({ children }: { children: React.ReactNode }) {
  const { message } = AntdApp.useApp();

  React.useEffect(() => {
    setAntdInstances({ message });
  }, [message]);

  return <>{children}</>;
}
// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  const hideChatSessionsMenu = initialState?.hiddenMenus?.includes(chatSessionsMenuKey);
  if (initialState?.currentUser?.fake_password === 1) {
    history.push('/account/password');
  }
  return {
    defaultCollapsed: true,
    breakpoint: false,
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatar || defualtAvatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => null,
    // 顶部「新建」固定；系统菜单 + 项目列表同层滚动；影响所有带 ProLayout 侧栏的页面
    menuContentRender: (props, defaultDom) => (
      <div className="session-history-sider-content">
        {!hideChatSessionsMenu && <SidebarCreateEntry collapsed={!!props?.collapsed} />}
        <div className="session-history-sider-scroll">
          <div className="session-history-sider-menu-wrap">{defaultDom}</div>
          {!hideChatSessionsMenu && <HistoryMenu collapsed={props?.collapsed} />}
        </div>
      </div>
    ),
    onPageChange: () => {
      if (sessionStorage.getItem('request_id')) {
        sessionStorage.removeItem('request_id');
      }
      if (sessionStorage.getItem('sseImgNumber')) {
        sessionStorage.removeItem('sseImgNumber');
      }
      if (sessionStorage.getItem('getMessageDetailCount')) {
        sessionStorage.removeItem('getMessageDetailCount');
      }

      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        setTimeout(() => {
          window.location.href = '/user/login';
        }, 1000 * 2);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#4953f8',
              colorInfo: '#4953f8',
              sizeStep: 4,
              colorError: '#f45554',
              colorWarning: '#ff7e01',
              colorSuccess: '#4da952',
            },
          }}
        >
          <AntdApp>
            <AntdAppProvider>
              <IframeListener />
              <NotificationBanner />
              <FloatButton.BackTop />
              {children}
            </AntdAppProvider>
          </AntdApp>
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  ...errorConfig,
};
